import { AnchorButton, Intent } from '@blueprintjs/core';
import * as go from 'gojs/release/go-module';
import * as React from 'react';
import { useRouteMatch } from 'react-router-dom';
import { Tooltip } from 'vet-bones/components';

import { ROUTE } from 'src/constants/routes';
import { TEST_IDS } from 'src/constants/testIds';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useDiagramSelection } from 'src/containers/ProjectDiagram/hooks/useDiagramSelectionValue';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { MappingLink } from 'src/containers/ProjectDiagram/types/diagram';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isMapping } from 'src/containers/ProjectDiagram/utils/data';
import { setDiagramSelection } from 'src/containers/ProjectDiagram/utils/setDiagramSelection';
import * as copy from 'src/templates/copy';
import { useNavigateMapping } from 'src/utils/hooks/useNavigateMapping';
import { useNavigateProject } from 'src/utils/hooks/useNavigateProject';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectNavbarToggle: React.VFC = () => {
  const { mappingId, projectId } = useRouterParams<'PROJECT_MAPPING'>();
  const diagram = useDiagram();
  const isUsingMappingRoute = Boolean(useRouteMatch(ROUTE.PROJECT_MAPPING));

  const selection = useDiagramSelection();

  const navigateProject = useNavigateProject();
  const handleNavigateToModel = React.useCallback(() => {
    if (diagram && mappingId) {
      const mappingData = diagram.model.linkDataArray.find(
        (linkData) => linkData.value === mappingId
      );
      const mappingLink = diagram.findLinkForKey(mappingData?.key);
      // select mapping, or clear selection
      commitDiagramWhileSkippingUndoManager(
        diagram,
        (_, diagram) =>
          setDiagramSelection(
            mappingLink ? new go.Set([mappingLink as go.Link]) : undefined
          )(diagram),
        DiagramTransaction.SET_DIAGRAM_SELECTION
      );
    }
    navigateProject(projectId);
  }, [diagram, mappingId, navigateProject, projectId]);

  const navigateMapping = useNavigateMapping();
  const handleNavigateToMapping = React.useCallback(() => {
    if (isUsingMappingRoute) {
      return;
    }
    // the first mapping found in the selection is used
    const mapping = selection
      .toArray()
      .find((part): part is MappingLink => isMapping(part.data));
    const mappingId = mapping?.data.value || null;

    navigateMapping(mappingId);
  }, [isUsingMappingRoute, navigateMapping, selection]);

  return (
    <div className="sd-project-navbar-toggle">
      <Tooltip content={copy.components.project.navbar.modelHover}>
        <AnchorButton
          data-testid={TEST_IDS.projectNavbar.toggle.modelViewButton}
          intent={isUsingMappingRoute ? Intent.NONE : Intent.PRIMARY}
          minimal
          onClick={handleNavigateToModel}
          outlined
          text={copy.components.project.navbar.model}
        />
      </Tooltip>
      <div className="sd-project-navbar-toggle__divider" />
      <Tooltip content={copy.components.project.navbar.mappingHover}>
        <AnchorButton
          data-testid={TEST_IDS.projectNavbar.toggle.mappingViewButton}
          intent={isUsingMappingRoute ? Intent.PRIMARY : Intent.NONE}
          minimal
          onClick={handleNavigateToMapping}
          outlined
          text={copy.components.project.navbar.mapping}
        />
      </Tooltip>
    </div>
  );
};
